import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDismiss } from './useDismiss';

import type {
  CellValue,
  ChipColor,
  FilterOperator,
  FilterRule,
  Layout,
  Property,
  PropertyOption,
  PropertyType,
  RowBackground,
  SortRule,
  View,
  ViewSettings,
} from '@noti/shared';

import {
  createDataSourceRow,
  deleteDataSourceRow,
  deleteDataSourceViewSettings,
  fetchDataSource,
  importDataSourceRows,
  listWorkspaceMembers,
  putDataSourceProperties,
  putDataSourceViews,
  putDataSourceViewSettings,
  renameDataSource,
  updateDataSourceRow,
  type DataSourceDetail,
  type DatabaseRowSummary,
} from '../api';
import { csvFileName, parseCsv, toCsv } from './database-csv';
import {
  asText,
  cellValue,
  DEFAULT_OPTIONS,
  OPTION_COLOR_CYCLE,
  OPERATORS,
  VALUELESS_OPERATORS,
  visibleProperties,
  visibleRows,
} from './database-rows';
import type { DatabaseRow } from './database-rows';
import { t } from '../i18n';
import { DatabaseProperties } from './DatabaseProperties';
import { LayoutView } from './DatabaseLayouts';
import { DatabaseTable, type RowAction } from './DatabaseTable';
import { PageIcon } from './PageIcon';
import { Glyph } from './icons';
import { Dropdown } from './DatabaseDropdown';
import { useToast } from './Toast';

/**
 * A database block: name, view tabs, the view toolbar and the panels behind
 * it. The table itself is `DatabaseTable`; the shared schema lives in
 * `DatabaseProperties`.
 */

export interface DatabaseProps {
  dataSourceId: string;
  workspaceId: string;
  onOpenRow: (rowId: string) => void;
  initialViewId?: string | null;
}

type Panel = 'filter' | 'sort' | 'group' | 'props' | 'view' | 'newview' | null;

const LAYOUTS: { id: Layout; label: string }[] = [
  { id: 'table', label: t('database.layoutTable') },
  { id: 'board', label: t('database.layoutBoard') },
  { id: 'calendar', label: t('database.layoutCalendar') },
  { id: 'list', label: t('database.layoutList') },
  { id: 'gallery', label: t('database.layoutGallery') },
];

const PANEL_TITLES: Record<string, string> = {
  filter: t('database.filter'),
  sort: t('database.sort'),
  group: t('database.group'),
  props: t('database.properties'),
  view: t('database.view'),
  newview: t('database.newView'),
};

/** Ids for views, filter and sort rules live in shared jsonb, so they are real
 *  uuids rather than something recognisable to the client. */
function newId(): string {
  return crypto.randomUUID();
}

function settingsOf(view: View): ViewSettings {
  return {
    filters: view.filters,
    fmode: view.fmode,
    sorts: view.sorts,
    hidden: view.hidden,
    widths: view.widths,
    group: view.group,
    calc: view.calc,
    month: view.month,
  };
}

function toRow(row: DatabaseRowSummary): DatabaseRow {
  return {
    id: row.id,
    title: row.title,
    icon: row.icon,
    color: row.color as RowBackground | null,
    values: row.values,
    createdAt: row.createdAt,
    editedAt: row.editedAt,
  };
}

export function Database({
  dataSourceId,
  workspaceId,
  onOpenRow,
  initialViewId,
}: DatabaseProps): React.ReactElement {
  const toast = useToast();
  const [source, setSource] = useState<DataSourceDetail | null>(null);
  const [failed, setFailed] = useState(false);
  const [viewId, setViewId] = useState<string | null>(initialViewId ?? null);
  const [draft, setDraft] = useState<ViewSettings | null>(null);
  useEffect(() => setViewId(initialViewId ?? null), [initialViewId]);
  const [panel, setPanel] = useState<Panel>(null);
  const closePanel = useCallback(() => setPanel(null), []);
  useDismiss(panel !== null, '[data-panel], [data-panel-trigger]', closePanel);
  const [query, setQuery] = useState('');
  const [people, setPeople] = useState<{ id: string; name: string }[]>([]);
  const [showCalc, setShowCalc] = useState(false);
  const [importing, setImporting] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const next = await fetchDataSource(dataSourceId);
      setSource(next);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [dataSourceId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    let active = true;
    void listWorkspaceMembers(workspaceId)
      .then((members) => {
        if (active) setPeople(members);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [workspaceId]);

  const baseView = source?.views.find((view) => view.id === viewId) ?? source?.views[0] ?? null;
  const view: View | null = baseView
    ? { ...baseView, ...(draft ?? source?.viewSettings ?? {}) }
    : null;

  const patchSettings = (change: Partial<ViewSettings>) => {
    if (!view) return;
    setDraft({ ...settingsOf(view), ...change });
  };

  const rows = useMemo(() => (source ? source.rows.map(toRow) : []), [source]);

  const groups = useMemo(
    () => (view ? visibleRows(rows, source?.properties ?? [], view, query) : []),
    [rows, source, view, query],
  );

  const saveProperties = async (properties: Property[]) => {
    setSource((current) => (current ? { ...current, properties } : current));
    try {
      await putDataSourceProperties(dataSourceId, properties);
      await refresh();
    } catch {
      toast(t('database.savePropertiesFailed'));
      await refresh();
    }
  };

  const patchProperty = (id: string, change: Partial<Property>) =>
    saveProperties(
      (source?.properties ?? []).map((item) => (item.id === id ? { ...item, ...change } : item)),
    );

  const writeCell = async (rowId: string, property: Property, value: CellValue) => {
    setSource((current) =>
      current
        ? {
            ...current,
            rows: current.rows.map((row) =>
              row.id === rowId
                ? property.type === 'title'
                  ? { ...row, title: asText(value) }
                  : { ...row, values: { ...row.values, [property.id]: value } }
                : row,
            ),
          }
        : current,
    );
    try {
      await updateDataSourceRow(dataSourceId, rowId, { propId: property.id, value });
      if (
        property.type === 'relation' ||
        source?.properties.some((candidate) => candidate.type === 'rollup')
      )
        await refresh();
    } catch {
      toast(t('database.saveCellFailed'));
      await refresh();
    }
  };

  const rowAction = async (rowId: string, action: RowAction) => {
    if (action === 'delete') {
      setSource((current) =>
        current ? { ...current, rows: current.rows.filter((row) => row.id !== rowId) } : current,
      );
      try {
        await deleteDataSourceRow(dataSourceId, rowId);
      } catch {
        toast(t('database.deleteRowFailed'));
        await refresh();
      }
      return;
    }
    if (action === 'duplicate') {
      const row = source?.rows.find((item) => item.id === rowId);
      if (!row) return;
      try {
        const editable = new Set(
          source?.properties
            .filter((property) => !['rollup', 'created', 'edited'].includes(property.type))
            .map((property) => property.id),
        );
        const values = Object.fromEntries(
          Object.entries(row.values).filter(([id]) => editable.has(id)),
        );
        await createDataSourceRow(dataSourceId, { values, afterId: rowId });
        await refresh();
        toast(t('database.rowDuplicated'));
      } catch {
        toast(t('database.duplicateRowFailed'));
      }
      return;
    }
    if (action === 'clear') {
      try {
        await updateDataSourceRow(dataSourceId, rowId, { clear: true });
        await refresh();
        toast(t('database.rowCleared'));
      } catch {
        toast(t('database.clearRowFailed'));
      }
      return;
    }
    const index = source?.rows.findIndex((row) => row.id === rowId) ?? -1;
    if (index < 0) return;
    const neighbour = action === 'above' ? source?.rows[index - 1] : source?.rows[index + 1];
    try {
      await createDataSourceRow(dataSourceId, {
        values: {},
        afterId: action === 'above' ? (neighbour?.id ?? null) : rowId,
      });
      await refresh();
    } catch {
      toast(t('database.addRowFailed'));
    }
  };

  if (failed) {
    return (
      <div className="dbcard dbcard--state">
        <p>{t('database.unavailable')}</p>
      </div>
    );
  }
  if (!source || !view) {
    return (
      <div className="dbcard dbcard--state">
        <p>{t('database.loading')}</p>
      </div>
    );
  }

  const properties: Property[] = source.properties.map((property) =>
    property.type === 'person'
      ? {
          ...property,
          options: [
            ...people.map((person) => ({ n: person.id, label: person.name, c: 'grey' as const })),
            ...property.options.filter(
              (option) => !people.some((person) => person.id === option.n),
            ),
          ],
        }
      : property.type === 'relation'
        ? {
            ...property,
            options: (source.relationOptions?.[property.id] ?? []).map((row) => ({
              n: row.id,
              label: row.title,
              c: 'grey' as const,
            })),
          }
        : property,
  );
  const title = properties.find((property) => property.type === 'title');
  const filterActive = view.filters.length > 0 || panel === 'filter';
  const sortActive = view.sorts.length > 0 || panel === 'sort';
  const groupActive = view.group !== null || panel === 'group';

  const addProperty = (name: string, type: PropertyType, afterId?: string | null) => {
    const property: Property = {
      id: newId(),
      name,
      type,
      options: DEFAULT_OPTIONS[type] ? [...DEFAULT_OPTIONS[type]] : [],
    };
    const next = [...properties];
    const at = afterId ? next.findIndex((item) => item.id === afterId) : -1;
    next.splice(at < 0 ? next.length : at + 1, 0, property);
    void saveProperties(next);
  };

  const toggleOption = (
    propertyId: string,
    mutate: (options: PropertyOption[]) => PropertyOption[],
  ) =>
    patchProperty(propertyId, {
      options: mutate(properties.find((item) => item.id === propertyId)?.options ?? []),
    });

  return (
    <div className="dbcard">
      <div className="dbcard__head">
        {source.icon ? (
          <span className="dbcard__headicon">
            <PageIcon icon={source.icon} />
          </span>
        ) : (
          <Glyph d="M4 4h16v16H4z M4 10h16 M9 4v16" size={18} className="dbcard__headicon" />
        )}
        <input
          className="ed dbcard__name"
          value={source.name}
          aria-label={t('database.name')}
          onChange={(event) => setSource({ ...source, name: event.target.value })}
          onBlur={(event) => {
            const name = event.target.value.trim();
            if (name && name !== source.name) {
              void renameDataSource(dataSourceId, name).catch(() =>
                toast(t('database.renameFailed')),
              );
            }
          }}
        />
      </div>

      <div className="dbcard__tabs">
        {source.views.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`row dbcard__tab${item.id === baseView?.id ? ' is-active' : ''}`}
            aria-pressed={item.id === baseView?.id}
            onClick={() => {
              setViewId(item.id);
              setDraft(null);
              setPanel(null);
            }}
          >
            {item.name}
          </button>
        ))}
        <button
          type="button"
          className="row dbcard__addview"
          aria-label={t('database.addView')}
          title={t('database.addView')}
          data-panel-trigger=""
          onClick={() => setPanel(panel === 'newview' ? null : 'newview')}
        >
          <Glyph d="M12 5v14M5 12h14" size={16} />
        </button>

        <span className="dbcard__spacer" />

        <button
          type="button"
          className={`row dbcard__tool${filterActive ? ' is-active' : ''}`}
          aria-pressed={filterActive}
          data-panel-trigger=""
          onClick={() => setPanel(panel === 'filter' ? null : 'filter')}
        >
          {view.filters.length
            ? t('database.filterCount', { count: String(view.filters.length) })
            : t('database.filter')}
        </button>
        <button
          type="button"
          className={`row dbcard__tool${sortActive ? ' is-active' : ''}`}
          aria-pressed={sortActive}
          data-panel-trigger=""
          onClick={() => setPanel(panel === 'sort' ? null : 'sort')}
        >
          {view.sorts.length
            ? t('database.sortCount', { count: String(view.sorts.length) })
            : t('database.sort')}
        </button>
        <button
          type="button"
          className={`row dbcard__tool${groupActive ? ' is-active' : ''}`}
          aria-pressed={groupActive}
          data-panel-trigger=""
          onClick={() => setPanel(panel === 'group' ? null : 'group')}
        >
          {view.group
            ? t('database.groupByName', {
                name: properties.find((item) => item.id === view.group)?.name ?? '',
              })
            : t('database.group')}
        </button>
        <button
          type="button"
          className={`row dbcard__tool${panel === 'props' ? ' is-active' : ''}`}
          aria-pressed={panel === 'props'}
          data-panel-trigger=""
          onClick={() => setPanel(panel === 'props' ? null : 'props')}
        >
          {t('database.properties')}
        </button>
        <button
          type="button"
          className={`row dbcard__tool${panel === 'view' ? ' is-active' : ''}`}
          aria-pressed={panel === 'view'}
          data-panel-trigger=""
          onClick={() => setPanel(panel === 'view' ? null : 'view')}
        >
          {t('database.view')}
        </button>
        <input
          className="ni dbcard__search"
          value={query}
          placeholder={t('database.search')}
          aria-label={t('database.searchLabel')}
          onChange={(event) => setQuery(event.target.value)}
        />
        {panel ? (
          <div
            className="dbcard__panel"
            role="dialog"
            aria-label={t('database.view')}
            data-panel=""
          >
            <div className="dbcard__paneltop">
              <span className="dbcard__eyebrow">{PANEL_TITLES[panel]}</span>
              <button
                type="button"
                className="row dbcard__panelclose"
                aria-label={t('database.close')}
                onClick={() => setPanel(null)}
              >
                ×
              </button>
            </div>

            {panel === 'filter' ? (
              <FilterPanel
                properties={properties}
                people={people}
                rules={view.filters}
                mode={view.fmode}
                onChange={(filters, fmode) => patchSettings({ filters, fmode })}
              />
            ) : null}

            {panel === 'sort' ? (
              <SortPanel
                properties={properties}
                sorts={view.sorts}
                onChange={(sorts) => patchSettings({ sorts })}
              />
            ) : null}
            {panel === 'group' ? (
              <GroupPanel
                properties={properties}
                group={view.group}
                onChange={(group) => patchSettings({ group })}
              />
            ) : null}
            {panel === 'props' ? (
              <DatabaseProperties
                properties={properties}
                hidden={view.hidden}
                onChange={(next) => void saveProperties(next)}
                onToggleVisible={(propertyId, shown) =>
                  patchSettings({
                    hidden: shown
                      ? view.hidden.filter((id) => id !== propertyId)
                      : [...view.hidden, propertyId],
                  })
                }
              />
            ) : null}
            {panel === 'view' && baseView ? (
              <ViewPanel
                view={baseView}
                views={source.views}
                showCalc={showCalc}
                onShowCalc={setShowCalc}
                onRename={(name) => {
                  const views = source.views.map((item) =>
                    item.id === baseView.id ? { ...item, name } : item,
                  );
                  void putDataSourceViews(dataSourceId, views);
                  setSource({ ...source, views });
                }}
                onLayout={(layout) => {
                  const views = source.views.map((item) =>
                    item.id === baseView.id ? { ...item, type: layout } : item,
                  );
                  void putDataSourceViews(dataSourceId, views);
                  setSource({ ...source, views });
                }}
                onDuplicate={() => {
                  const copy: View = { ...baseView, id: newId(), name: `${baseView.name} copy` };
                  const at = source.views.findIndex((item) => item.id === baseView.id);
                  const views = [...source.views];
                  views.splice(at + 1, 0, copy);
                  void putDataSourceViews(dataSourceId, views);
                  setSource({ ...source, views });
                  setViewId(copy.id);
                }}
                importing={importing}
                onExportCsv={() => {
                  const rows = groups.flatMap((group) => group.rows);
                  const csv = toCsv(rows, visibleProperties(properties, view));
                  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = csvFileName(source.name);
                  link.click();
                  URL.revokeObjectURL(url);
                  toast(t('database.exportedRows', { count: String(rows.length) }));
                }}
                onImportCsv={(file) => {
                  setImporting(true);
                  void file
                    .text()
                    .then((text) => parseCsv(text))
                    .then(async (parsed) => {
                      if (parsed.length === 0) {
                        toast(t('database.noRowsFound'));
                        return;
                      }
                      const imported = await importDataSourceRows(dataSourceId, parsed);
                      await refresh();
                      toast(t('database.importedRows', { count: String(imported) }));
                    })
                    .catch(() => toast(t('database.importFailed')))
                    .finally(() => setImporting(false));
                }}
                onDelete={() => {
                  if (source.views.length < 2) {
                    toast(t('database.needOneView'));
                    return;
                  }
                  const views = source.views.filter((item) => item.id !== baseView.id);
                  void putDataSourceViews(dataSourceId, views);
                  setSource({ ...source, views });
                  setViewId(views[0]?.id ?? null);
                }}
              />
            ) : null}
            {panel === 'newview' ? (
              <div className="dbcard__layouts">
                {LAYOUTS.map((layout) => (
                  <button
                    key={layout.id}
                    type="button"
                    className="row dbcard__layout"
                    onClick={() => {
                      const created: View = {
                        id: newId(),
                        name: layout.label,
                        type: layout.id,
                        filters: [],
                        fmode: 'and',
                        sorts: [],
                        hidden: [],
                        widths: {},
                        group: null,
                        calc: {},
                        month: null,
                      };
                      void putDataSourceViews(dataSourceId, [...source.views, created]);
                      setSource({ ...source, views: [...source.views, created] });
                      setViewId(created.id);
                      setPanel(null);
                    }}
                  >
                    {layout.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {draft || source.viewSettings ? (
        <ViewBar
          hasDraft={draft !== null}
          onReset={() => setDraft(null)}
          onSaveForMe={() => {
            if (!draft) return;
            void putDataSourceViewSettings(dataSourceId, draft)
              .then(() => {
                setSource({ ...source, viewSettings: draft });
                setDraft(null);
                toast(t('database.savedForYou'));
              })
              .catch(() => toast(t('database.viewSettingsFailed')));
          }}
          onSaveForEveryone={() => {
            if (!baseView || !draft) return;
            const views = source.views.map((item) =>
              item.id === baseView.id ? { ...item, ...draft } : item,
            );
            void putDataSourceViews(dataSourceId, views)
              .then(() => deleteDataSourceViewSettings(dataSourceId).catch(() => undefined))
              .then(() => {
                setSource({ ...source, views, viewSettings: null });
                setDraft(null);
                toast(t('database.savedForEveryone'));
              })
              .catch(() => toast(t('database.saveViewFailed')));
          }}
          onBackToShared={() => {
            void deleteDataSourceViewSettings(dataSourceId)
              .then(() => {
                setSource({ ...source, viewSettings: null });
                setDraft(null);
                toast(t('database.backToShared'));
              })
              .catch(() => toast(t('database.resetViewFailed')));
          }}
        />
      ) : null}

      {view.type === 'table' ? (
        <DatabaseTable
          relationOptions={source.relationOptions}
          properties={properties}
          view={view}
          groups={groups}
          people={people}
          showCalc={showCalc}
          onWriteCell={(rowId, property, value) => void writeCell(rowId, property, value)}
          onRowColor={(rowId: string, color: RowBackground | null) => {
            setSource((current) =>
              current
                ? {
                    ...current,
                    rows: current.rows.map((row) => (row.id === rowId ? { ...row, color } : row)),
                  }
                : current,
            );
            void updateDataSourceRow(dataSourceId, rowId, { color }).catch(() => {
              toast(t('database.rowColorFailed'));
              void refresh();
            });
          }}
          onRowAction={(rowId, action) => void rowAction(rowId, action)}
          onOpenRow={onOpenRow}
          onWidthChange={(propertyId, width) =>
            patchSettings({ widths: { ...view.widths, [propertyId]: width } })
          }
          onSort={(rule) =>
            patchSettings({
              sorts: [{ id: newId(), ...rule }],
            })
          }
          onFilter={(propertyId) => {
            const property = properties.find((item) => item.id === propertyId);
            if (!property) return;
            patchSettings({
              filters: [
                ...view.filters,
                { id: newId(), propId: propertyId, op: OPERATORS[property.type][0]![0], val: '' },
              ],
            });
            setPanel('filter');
          }}
          onGroupBy={(propertyId) => patchSettings({ group: propertyId })}
          onHide={(propertyId) => patchSettings({ hidden: [...view.hidden, propertyId] })}
          onColumnColor={(propertyId, color) =>
            patchProperty(propertyId, { color: color ?? undefined })
          }
          onColumnType={(propertyId, type) => {
            const property = properties.find((item) => item.id === propertyId);
            const keep = title && property?.type === 'title' ? view.hidden : [...view.hidden];
            patchProperty(propertyId, {
              type,
              options: DEFAULT_OPTIONS[type] ? [...DEFAULT_OPTIONS[type]] : [],
            });
            if (property?.type === 'title') patchSettings({ hidden: keep });
          }}
          onRenameColumn={(propertyId, name) => patchProperty(propertyId, { name })}
          onOptionsText={(propertyId, text) =>
            patchProperty(propertyId, {
              options: text
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean)
                .map((value, index) => {
                  const existing = properties
                    .find((item) => item.id === propertyId)
                    ?.options.find((option) => option.n === value);
                  return {
                    n: value,
                    c: existing?.c ?? OPTION_COLOR_CYCLE[index % OPTION_COLOR_CYCLE.length]!,
                  };
                }),
            })
          }
          onClearColumn={(propertyId) => {
            const property = properties.find((item) => item.id === propertyId);
            if (!property) return;
            const affected = new Set(
              rows
                .filter((row) => {
                  const value = cellValue(row, property);
                  return asText(value) !== '';
                })
                .map((row) => row.id),
            );
            setSource((current) =>
              current
                ? {
                    ...current,
                    rows: current.rows.map((row) =>
                      affected.has(row.id)
                        ? property.type === 'title'
                          ? { ...row, title: '' }
                          : { ...row, values: { ...row.values, [propertyId]: '' } }
                        : row,
                    ),
                  }
                : current,
            );
            void Promise.all(
              rows
                .filter((row) => affected.has(row.id))
                .map((row) =>
                  updateDataSourceRow(dataSourceId, row.id, {
                    propId: propertyId,
                    value: '',
                  }).catch(() => undefined),
                ),
            );
          }}
          onDuplicateColumn={(propertyId) => {
            const property = properties.find((item) => item.id === propertyId);
            if (!property) return;
            addProperty(`${property.name} copy`, property.type, propertyId);
          }}
          onInsertColumn={(afterId) => {
            const name = `Property ${properties.length}`;
            addProperty(name, 'text', afterId);
          }}
          onDeleteColumn={(propertyId) =>
            void saveProperties(properties.filter((item) => item.id !== propertyId))
          }
          onAddRow={() => {
            const values: Record<string, CellValue> = {};
            if (title) values[title.id] = '';
            void createDataSourceRow(dataSourceId, { values })
              .then(() => refresh())
              .catch(() => toast('Could not add a row'));
          }}
          onAddProperty={(name, type) => addProperty(name, type)}
          onCalcChange={(propertyId, kind) =>
            patchSettings({ calc: { ...view.calc, [propertyId]: kind } })
          }
          onRenameOption={(propertyId, value, name) =>
            toggleOption(propertyId, (options) =>
              options.map((option) => (option.n === value ? { ...option, n: name } : option)),
            )
          }
          onRecolorOption={(propertyId, value, color: ChipColor) =>
            toggleOption(propertyId, (options) =>
              options.map((option) => (option.n === value ? { ...option, c: color } : option)),
            )
          }
          onRemoveOption={(propertyId, value) =>
            toggleOption(propertyId, (options) => options.filter((option) => option.n !== value))
          }
          onCreateOption={(propertyId, name) =>
            toggleOption(propertyId, (options) => [
              ...options,
              { n: name, c: OPTION_COLOR_CYCLE[options.length % OPTION_COLOR_CYCLE.length]! },
            ])
          }
        />
      ) : (
        <LayoutView
          layout={view.type}
          rows={rows}
          properties={properties}
          view={view}
          query={query}
          onOpenRow={onOpenRow}
          onWriteCell={(rowId, property, value) => void writeCell(rowId, property, value)}
          onAddRow={(values: Record<string, string>) =>
            void createDataSourceRow(dataSourceId, { values })
              .then(() => refresh())
              .catch(() => toast(t('database.addRowFailed')))
          }
          onRowColor={(rowId: string, color: RowBackground | null) => {
            setSource((current) =>
              current
                ? {
                    ...current,
                    rows: current.rows.map((row) => (row.id === rowId ? { ...row, color } : row)),
                  }
                : current,
            );
            void updateDataSourceRow(dataSourceId, rowId, { color }).catch(() => {
              toast(t('database.rowColorFailed'));
              void refresh();
            });
          }}
        />
      )}
    </div>
  );
}

function ViewBar({
  hasDraft,
  onReset,
  onSaveForMe,
  onSaveForEveryone,
  onBackToShared,
}: {
  hasDraft: boolean;
  onReset: () => void;
  onSaveForMe: () => void;
  onSaveForEveryone: () => void;
  onBackToShared: () => void;
}): React.ReactElement {
  return (
    <div className="dbvbar" role="status">
      <span className="dbvbar__text">
        {hasDraft ? t('database.changesDraft') : t('database.personalSettings')}
      </span>
      <button
        type="button"
        className="row dbvbar__btn"
        onClick={hasDraft ? onReset : onBackToShared}
      >
        {hasDraft ? t('database.reset') : t('database.backToShared')}
      </button>
      {hasDraft ? (
        <button
          type="button"
          className="row dbvbar__btn dbvbar__btn--outline"
          onClick={onSaveForMe}
        >
          {t('database.saveForMe')}
        </button>
      ) : null}
      <button
        type="button"
        className="prim dbvbar__btn dbvbar__btn--primary"
        onClick={onSaveForEveryone}
      >
        {t('database.saveForEveryone')}
      </button>
    </div>
  );
}

function FilterPanel({
  properties,
  people,
  rules,
  mode,
  onChange,
}: {
  properties: Property[];
  people: { id: string; name: string }[];
  rules: FilterRule[];
  mode: 'and' | 'or';
  onChange: (rules: FilterRule[], mode: 'and' | 'or') => void;
}): React.ReactElement {
  const first = properties.find((property) => property.type !== 'title') ?? properties[0];

  const update = (id: string, change: Partial<FilterRule>) =>
    onChange(
      rules.map((rule) => (rule.id === id ? { ...rule, ...change } : rule)),
      mode,
    );

  return (
    <>
      {rules.map((rule) => {
        const property = properties.find((item) => item.id === rule.propId) ?? first;
        const operators = property ? OPERATORS[property.type] : [];
        const needsValue = !VALUELESS_OPERATORS.includes(rule.op);
        return (
          <div className="dbpanel__rule" key={rule.id}>
            <Dropdown
              label={t('database.propertyName')}
              block
              value={rule.propId}
              options={(property ? [property] : []).map((item) => ({
                value: item.id,
                label: item.name,
              }))}
              onPick={(value) => {
                const next = properties.find((item) => item.id === value);
                update(rule.id, {
                  propId: value,
                  op: next ? OPERATORS[next.type][0]![0] : rule.op,
                  val: '',
                });
              }}
            />
            <Dropdown
              label={t('database.condition')}
              block
              value={rule.op}
              options={operators.map(([value, text]) => ({ value, label: text }))}
              onPick={(value) => update(rule.id, { op: value as FilterOperator, val: '' })}
            />
            {needsValue && property ? (
              property.options.length > 0 || property.type === 'person' ? (
                <Dropdown
                  label={t('database.value')}
                  block
                  value={rule.val}
                  options={[
                    { value: '', label: '—' },
                    ...(property.type === 'person'
                      ? people.map((person) => ({ value: person.id, label: person.name }))
                      : property.options.map((option) => ({
                          value: option.n,
                          label: option.label ?? option.n,
                        }))),
                  ]}
                  onPick={(value) => update(rule.id, { val: value })}
                />
              ) : (
                <input
                  className="ni dbpanel__value"
                  value={rule.val}
                  aria-label={t('database.value')}
                  onChange={(event) => update(rule.id, { val: event.target.value })}
                />
              )
            ) : null}
            <button
              type="button"
              className="row dbpanel__remove"
              aria-label={t('database.deleteFilter')}
              onClick={() =>
                onChange(
                  rules.filter((item) => item.id !== rule.id),
                  mode,
                )
              }
            >
              ×
            </button>
          </div>
        );
      })}
      {rules.length === 0 ? (
        <span className="dbpanel__empty">{t('database.noFilters')}</span>
      ) : null}
      {rules.length > 1 ? (
        <button
          type="button"
          className="row dbpanel__mode"
          onClick={() => onChange(rules, mode === 'and' ? 'or' : 'and')}
        >
          {mode === 'or' ? t('database.matchAny') : t('database.matchAll')}
        </button>
      ) : null}
      <button
        type="button"
        className="row dbpanel__add"
        onClick={() =>
          onChange(
            [
              ...rules,
              {
                id: newId(),
                propId: first?.id ?? '',
                op: first ? OPERATORS[first.type][0]![0] : 'eq',
                val: '',
              },
            ],
            mode,
          )
        }
      >
        {t('database.addFilter')}
      </button>
    </>
  );
}

function SortPanel({
  properties,
  sorts,
  onChange,
}: {
  properties: Property[];
  sorts: SortRule[];
  onChange: (sorts: SortRule[]) => void;
}): React.ReactElement {
  return (
    <>
      {sorts.map((sort) => {
        return (
          <div className="dbpanel__rule" key={sort.id}>
            <Dropdown
              label={t('database.sortBy')}
              value={sort.propId}
              options={properties.map((item) => ({ value: item.id, label: item.name }))}
              onPick={(value) =>
                onChange(
                  sorts.map((item) => (item.id === sort.id ? { ...item, propId: value } : item)),
                )
              }
            />
            <button
              type="button"
              className="row dbpanel__dir"
              onClick={() =>
                onChange(
                  sorts.map((item) =>
                    item.id === sort.id
                      ? { ...item, dir: item.dir === 'asc' ? 'desc' : 'asc' }
                      : item,
                  ),
                )
              }
            >
              {sort.dir === 'asc' ? t('database.ascending') : t('database.descending')}
            </button>
            <button
              type="button"
              className="row dbpanel__remove"
              aria-label={t('database.removeSort')}
              onClick={() => onChange(sorts.filter((item) => item.id !== sort.id))}
            >
              ×
            </button>
          </div>
        );
      })}
      {sorts.length === 0 ? <span className="dbpanel__empty">{t('database.noSorts')}</span> : null}
      <button
        type="button"
        className="row dbpanel__add"
        onClick={() => {
          const unused = properties.find((item) => !sorts.some((sort) => sort.propId === item.id));
          if (!unused) return;
          onChange([...sorts, { id: newId(), propId: unused.id, dir: 'asc' }]);
        }}
      >
        {t('database.addSort')}
      </button>
    </>
  );
}

function GroupPanel({
  properties,
  group,
  onChange,
}: {
  properties: Property[];
  group: string | null;
  onChange: (group: string | null) => void;
}): React.ReactElement {
  const groupable = properties.filter((property) =>
    ['select', 'status', 'person', 'checkbox'].includes(property.type),
  );
  return (
    <Dropdown
      label={t('database.groupBy')}
      block
      value={group ?? ''}
      options={[
        { value: '', label: t('database.none') },
        ...groupable.map((property) => ({ value: property.id, label: property.name })),
      ]}
      onPick={(value) => onChange(value === '' ? null : value)}
    />
  );
}

function ViewPanel({
  view,
  views,
  showCalc,
  onShowCalc,
  onRename,
  onLayout,
  onDuplicate,
  onDelete,
  onExportCsv,
  onImportCsv,
  importing,
}: {
  view: View;
  views: View[];
  showCalc: boolean;
  onShowCalc: (value: boolean) => void;
  onRename: (name: string) => void;
  onLayout: (layout: Layout) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onExportCsv: () => void;
  onImportCsv: (file: File) => void;
  importing: boolean;
}): React.ReactElement {
  return (
    <>
      <input
        className="ni dbpanel__input"
        value={view.name}
        aria-label={t('database.viewName')}
        onChange={(event) => onRename(event.target.value)}
      />
      <div className="dbcard__layouts">
        {LAYOUTS.map((layout) => (
          <button
            key={layout.id}
            type="button"
            className={`row dbcard__layout${view.type === layout.id ? ' is-active' : ''}`}
            aria-pressed={view.type === layout.id}
            onClick={() => onLayout(layout.id)}
          >
            {layout.label}
          </button>
        ))}
      </div>
      <div className="dbcard__layouts">
        <button type="button" className="row dbpanel__outline" onClick={onDuplicate}>
          {t('database.duplicateView')}
        </button>
        <button
          type="button"
          className="row dbpanel__outline"
          aria-pressed={showCalc}
          onClick={() => onShowCalc(!showCalc)}
        >
          {showCalc ? t('database.hideCalculations') : t('database.showCalculations')}
        </button>
      </div>
      <div className="dbcard__layouts">
        <button type="button" className="row dbpanel__outline" onClick={onExportCsv}>
          {t('database.exportCsv')}
        </button>
      </div>
      <label className="dbpanel__import">
        {t('database.importCsvLabel')}
        <input
          type="file"
          accept=".csv,text/csv"
          aria-label={t('database.importCsv')}
          disabled={importing}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onImportCsv(file);
            event.target.value = '';
          }}
        />
      </label>
      <button
        type="button"
        className="row dbpanel__delete"
        onClick={onDelete}
        disabled={views.length < 2}
      >
        {t('database.deleteView')}
      </button>
    </>
  );
}
